import classNames from "classnames";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type PropsWithChildren,
  type Ref
} from "react";
import NewTabIcon from "../../../Blocks/ProvetOnlineBooking/components/shared/newTabIcon";
import { Svg } from "../../../Shared/Assets/Svg/React";
import { ButtonColor } from "./buttonColor";
import { ButtonSize } from "./buttonSize";
import { ButtonTarget } from "./buttonTarget";
import { ButtonType } from "./buttonType";
import {
  getCssClass,
  getIconClass,
  getInnerContainerClass,
  getSpinnerClass,
  mapButtonSizeToSvgSize
} from "./buttonUtlility";
import { ButtonVariant } from "./buttonVariant";

/**
 * Props for the Button component.
 * @interface ButtonProps
 */
interface ButtonProps {
  id?: string;
  onClick?: (e?: any) => void | Promise<void>;
  size: ButtonSize;
  disabled?: boolean;
  href?: string;
  target?: ButtonTarget;
  additionalClass?: string;
  name?: string;
  title?: string;
  baseClass?: string;
  icon?: string;
  color?: ButtonColor;
  sizeSM?: ButtonSize;
  sizeMD?: ButtonSize;
  sizeLG?: ButtonSize;
  sizeXL?: ButtonSize;
  variant?: ButtonVariant;
  type?: ButtonType;
  iconRotated?: boolean;
  iconTransition?: boolean;
  newWindowScreenReaderText?: string;
  isLoading?: boolean;
}

type ButtonPropsWithDataAttributes = ButtonProps &
  Record<`data-${string}`, string | undefined>;

export const Button = forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  PropsWithChildren<ButtonPropsWithDataAttributes>
>(
  (
    {
      id,
      onClick,
      title,
      href,
      target,
      disabled,
      name,
      icon,
      children,
      color = ButtonColor.DarkBlue,
      size,
      variant = ButtonVariant.Normal,
      additionalClass,
      sizeSM,
      sizeMD,
      sizeLG,
      sizeXL,
      baseClass = "",
      type,
      iconRotated = false,
      iconTransition = false,
      newWindowScreenReaderText,
      isLoading: externalIsLoading,
      ...rest
    },
    ref
  ) => {
    // Extract data attributes from rest props
    const dataAttributes = Object.keys(rest).reduce(
      (acc, key) => {
        if (key.startsWith("data-")) {
          acc[key] = rest[key as keyof typeof rest] as string;
        }
        return acc;
      },
      {} as Record<string, string>
    );
    const [internalIsLoading, setInternalIsLoading] = useState(false);

    const isMounted = useRef(true);
    useEffect(() => {
      isMounted.current = true;

      return () => {
        isMounted.current = false;
      };
    }, []);

    // Use external loading state if provided, otherwise use internal state
    const isLoading =
      externalIsLoading !== undefined ? externalIsLoading : internalIsLoading;

    const getIconWrapperStyle = (): CSSProperties => {
      const styles: CSSProperties = {};

      if (iconTransition) {
        styles.transition = "transform 250ms ease-in-out";
      }

      if (iconRotated) {
        styles.transform = "rotate(180deg)";
      }

      return styles;
    };

    const handleClick = useCallback(
      async (e: any) => {
        if (isLoading || disabled) {
          return;
        }

        if (onClick) {
          const result = onClick(e);
          if (result instanceof Promise) {
            setInternalIsLoading(true);
            try {
              await result;
            } finally {
              if (isMounted.current) {
                setInternalIsLoading(false);
              }
            }
          }
        }
      },
      [onClick, isLoading, disabled]
    );

    const isDisabled = disabled || isLoading;

    if (!!href) {
      return (
        <a
          id={id}
          ref={ref as Ref<HTMLAnchorElement>}
          className={getCssClass(
            baseClass,
            color,
            additionalClass,
            size,
            variant,
            sizeSM,
            sizeMD,
            sizeLG,
            sizeXL,
            isLoading
          )}
          href={href}
          target={target}
          title={title}
          {...dataAttributes}
        >
          <div
            className={classNames(
              getInnerContainerClass(size, sizeSM, sizeMD, sizeLG, sizeXL),
              {
                "gap-xs": !!icon && isLoading
              }
            )}
          >
            {!!icon && icon?.length > 0 && (
              <Svg
                href={icon}
                size={mapButtonSizeToSvgSize(size)}
                additionalClass={getIconClass(size, sizeSM, sizeMD, sizeLG, sizeXL)}
              />
            )}
            {children}
            {!!target && target === ButtonTarget.Blank && (
              <NewTabIcon screenReaderText={newWindowScreenReaderText} />
            )}
          </div>
        </a>
      );
    } else {
      return (
        <button
          id={id}
          ref={ref as Ref<HTMLButtonElement>}
          onClick={handleClick}
          className={getCssClass(
            baseClass,
            color,
            additionalClass,
            size,
            variant,
            sizeSM,
            sizeMD,
            sizeLG,
            sizeXL,
            isLoading
          )}
          disabled={isDisabled}
          name={name}
          title={title}
          type={type || ButtonType.Button}
          {...dataAttributes}
        >
          <div
            className={classNames(
              getInnerContainerClass(size, sizeSM, sizeMD, sizeLG, sizeXL),
              {
                "gap-xs": !!icon || isLoading
              }
            )}
          >
            {!!icon && icon?.length > 0 && (
              <div className="relative aspect-square">
                <span
                  className={classNames("flex justify-center", {
                    "opacity-0": isLoading
                  })}
                  style={getIconWrapperStyle()}
                >
                  <Svg href={icon} size={mapButtonSizeToSvgSize(size)} />
                </span>
                <div
                  className={classNames(
                    getSpinnerClass(isLoading, true),
                    getIconClass(size, sizeSM, sizeMD, sizeLG, sizeXL)
                  )}
                ></div>
              </div>
            )}
            {!icon && isLoading && (
              <div
                className={classNames(
                  getSpinnerClass(isLoading, false),
                  getIconClass(size, sizeSM, sizeMD, sizeLG, sizeXL)
                )}
              ></div>
            )}
            {children && <span className="flex justify-center">{children}</span>}
          </div>
        </button>
      );
    }
  }
);

Button.displayName = "Button";

export { ButtonColor, ButtonSize, ButtonTarget, ButtonType, ButtonVariant };
export default Button;
